import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { http } from '../../lib/api';
import { AppLayout } from '../../components/layout';
import { Button, Card, StatusBadge, MetricCard, CopyButton, EmptyState, Input, Select, Textarea } from '../../components/core';
import { DataTable, Modal, type Column, SpinnerBlock } from '../../components/feedback';
import { ChartCard, Spectrogram, PALETTE, type ChartData } from '../../components/charts';
import { ADMIN_NAV } from './nav';
import { useUiStore } from '../../store/ui';
import { BILLING_CYCLE_DAYS, priceForCycle } from '@livestreamx/shared-types';

interface TenantStream {
  id: string; name: string; status: string; recordingEnabled: boolean;
  startedAt: string | null; endedAt: string | null; durationSeconds: number;
  totalBytes: number; totalDurationSeconds: number; peakBitrateKbps: number | null;
  avgBitrateKbps: number | null; destinations: number;
  ingestUrl: string; hlsUrl: string; playerUrl: string; embedUrl: string;
  /** True when an ACTIVE (unexpired) subscription covers this stream. */
  planActive: boolean;
  planId?: string | null;
  planName?: string | null;
  renewsAt?: string | null;
  billingCycle?: 'DAILY' | 'MONTHLY' | 'YEARLY' | null;
  lastPaidAmount?: number | null;
  lastPaidCurrency?: string | null;
}
interface DailyPoint { date: string; sessions: number; durationSeconds: number; bytes: number; }
interface TenantSubscription {
  id: string; planId: string; name: string; status: string;
  /** Null = tenant-wide row covering every stream on this plan. */
  streamId?: string | null;
  billingCycle: string; startAt: string | null; endAt: string | null; expired: boolean;
  /** Plan row data — prices/status survive the plan leaving the ACTIVE catalogue. */
  planStatus: string; currency: string;
  priceMonth: number; priceYear: number | null; priceDay: number | null;
  /** Latest money actually collected against this row; null when never charged. */
  lastPaid: { amount: number; currency: string; paidAt: string; method: string | null } | null;
}
interface PlanLimits {
  maxStreams: number; maxDestinationsPerStream: number; maxTotalDestinations: number;
  maxStorageGb: number; maxBandwidthGb: number; recordingEnabled: boolean; maxInboundBitrateMbps: number;
  maxOutboundBitrateMbps: number;
}
interface PlanOption {
  id: string; name: string; status: string; priceMonth: number; priceYear: number | null; priceDay: number | null;
  maxStreams: number; maxDestinationsPerStream: number; maxTotalDestinations: number;
  currency?: string;
}
interface TenantDetail {
  tenant: { id: string; name: string; slug: string; status: string; createdAt: string };
  users: { id: string; email: string | null; role: string }[];
  subscriptions: TenantSubscription[];
  limits: PlanLimits;
  streams: TenantStream[];
  activeDestinations: number;
  daily: DailyPoint[];
  totals: { streams: number; liveStreams: number; sessions: number; durationSeconds: number; bytes: number; paid: number };
  payments: {
    id: string; amount: number; currency: string; status: string; provider: string;
    providerRef: string | null; planName: string | null; billingCycle: string | null;
    streamName: string | null;
    method: string | null; paidAt: string | null; note: string | null;
    receipt: string | null; createdAt: string;
  }[];
}

const PAY_METHODS = [
  { value: 'UPI', label: 'UPI' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer (NEFT/IMPS/RTGS)' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

type Cycle = 'DAILY' | 'MONTHLY' | 'YEARLY';

/**
 * What the payment modal is for. `mode` only changes wording: both paths call
 * the same endpoint, which extends the existing subscription instead of
 * resetting its clock, so a renewal is a grant on a plan the tenant already has.
 */
interface Granting {
  plan: PlanOption;
  cycle: Cycle;
  mode: 'grant' | 'renew';
  /** Which subscription row this renews. Absent = tenant-wide row for the plan. */
  streamId?: string | null;
  /**
   * The row being renewed — its plan and latest payment are the modal's
   * "previous plan & amount" context, and when `plan` differs from it the
   * submit is a change of plan rather than a plain renewal.
   */
  sub?: TenantSubscription | null;
}

/** Price for a cycle; `priceForCycle` falls back to the monthly rate (÷30/day, ×12/year) when the cycle has no price. */
const priceFor = (p: PlanOption, cycle: Cycle) => priceForCycle(p, cycle);

/** Intl currency label; unknown/invalid codes degrade to "amount CODE" instead of throwing. */
const money = (amount: number, currency?: string | null) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'INR').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency ?? ''}`.trim();
  }
};

/**
 * The subscription row's own plan as a PlanOption. The catalogue dropdown only
 * holds ACTIVE plans, so an archived plan used to make "Renew…" a dead button —
 * the row now carries the plan's price fields, enough to price any renewal.
 * The max* limits are dummies: renewal copy never renders them.
 */
const planOptionFromSub = (s: TenantSubscription): PlanOption => ({
  id: s.planId,
  name: s.name,
  status: s.planStatus,
  currency: s.currency,
  priceMonth: s.priceMonth,
  priceYear: s.priceYear,
  priceDay: s.priceDay,
  maxStreams: 0,
  maxDestinationsPerStream: 0,
  maxTotalDestinations: 0,
});

/** "BANK_TRANSFER" -> "Bank transfer"; unknown values pass through untouched. */
const methodLabel = (m: string | null) =>
  m ? (PAY_METHODS.find((o) => o.value === m)?.label ?? m.replace(/_/g, ' ').toLowerCase()) : '—';

const hrs = (s: number) => (s / 3600).toLocaleString(undefined, { maximumFractionDigits: 1 });
const mb = (b: number) => (b / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 });
const dayLabel = (d: string) => new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtDur = (sec: number) => {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60);
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};
const longDate = (d: string) => new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export function AdminTenantDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ui = useUiStore();
  const [data, setData] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Password reset modal state: which member is targeted + the new password.
  const [resetFor, setResetFor] = useState<{ id: string; email: string | null } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  // Plan management: the catalogue to add from, and which row is mid-request.
  // `granting` is non-null only while the payment modal is open — activating a
  // plan always goes through it so every grant leaves a transaction record.
  const [granting, setGranting] = useState<Granting | null>(null);
  // Stream renewal from the "no active plan" list. `renewing` is
  // non-null only while the modal is open; the cycle starts at whatever
  // the stream was on before (MONTHLY when it never had one), and
  // `planId` is the plan picker's current selection — the stream's own
  // plan until the admin picks another one (a change of plan).
  const [renewing, setRenewing] = useState<{ stream: TenantStream; cycle: Cycle; planId: string | null } | null>(null);
  const [pay, setPay] = useState({
    collectPayment: true, amount: '', method: 'BANK_TRANSFER',
    reference: '', paidAt: new Date().toISOString().slice(0, 10), note: '',
  });
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [planToAdd, setPlanToAdd] = useState('');
  const [planBusy, setPlanBusy] = useState('');
  // Manual stream creation for a plan that has no stream yet (tenant-wide row).
  // `creatingFor` is non-null only while the modal is open.
  const [creatingFor, setCreatingFor] = useState<TenantSubscription | null>(null);
  const [streamName, setStreamName] = useState('');
  const [streamBusy, setStreamBusy] = useState(false);

  /**
   * Open the payment modal prefilled for a grant or a renewal. A renewal
   * starts at the amount LAST COLLECTED for the row (the same rule the
   * tenant's own RenewDialog shows); a fresh grant starts at the plan price.
   */
  const openPayment = (
    plan: PlanOption, cycle: Cycle, mode: Granting['mode'], streamId?: string | null,
    sub?: TenantSubscription | null,
  ) => {
    const lastPaid = mode === 'renew' && sub?.lastPaid ? sub.lastPaid.amount : null;
    setPay({
      collectPayment: true,
      amount: String(lastPaid ?? priceFor(plan, cycle)),
      method: 'BANK_TRANSFER',
      reference: '',
      paidAt: new Date().toISOString().slice(0, 10),
      note: '',
    });
    setGranting({ plan, cycle, mode, streamId, sub: sub ?? null });
  };

  useEffect(() => {
    setLoading(true);
    http.get<TenantDetail>(`/api/admin/tenants/${id}`)
      .then(setData)
      .catch((e) => setError(e.message ?? 'Failed to load tenant'))
      .finally(() => setLoading(false));
    http.get<PlanOption[]>('/api/admin/plans')
      .then((rows) => setPlans(rows.filter((p) => p.status === 'ACTIVE')))
      .catch(() => setPlans([]));
  }, [id]);

  const spectroDays = useMemo(
    () => (data?.daily ?? []).map((d) => ({
      date: d.date,
      value: d.durationSeconds,
      tooltip: `${dayLabel(d.date)} · ${d.sessions} session${d.sessions === 1 ? '' : 's'} · ${hrs(d.durationSeconds)} h · ${mb(d.bytes)} MB`,
    })),
    [data],
  );

  const dailyHours = useMemo<ChartData | null>(() => {
    if (!data?.daily.some((d) => d.durationSeconds > 0)) return null;
    return { type: 'line', labels: data.daily.map((d) => dayLabel(d.date)), values: data.daily.map((d) => Number(hrs(d.durationSeconds))), label: 'Hours streamed', yLabel: 'Hours', color: '#6366f1' };
  }, [data]);

  const dailyData = useMemo<ChartData | null>(() => {
    if (!data?.daily.some((d) => d.bytes > 0)) return null;
    return { type: 'bar', labels: data.daily.map((d) => dayLabel(d.date)), values: data.daily.map((d) => Number(mb(d.bytes))), label: 'Data', yLabel: 'MB', color: '#06b6d4' };
  }, [data]);

  // Comparison doughnuts: bandwidth share per stream (all-time, answers "who eats
  // the pipe") and the live/offline split.
  const streamMix = useMemo<ChartData | null>(() => {
    const withBytes = (data?.streams ?? []).filter((s) => s.totalBytes > 0);
    if (!withBytes.length) return null;
    return { type: 'doughnut', labels: withBytes.map((s) => s.name), values: withBytes.map((s) => Number(mb(s.totalBytes))), label: 'Bandwidth share', colors: PALETTE };
  }, [data]);

  const statusMix = useMemo<ChartData | null>(() => {
    const m = new Map<string, number>();
    for (const s of data?.streams ?? []) m.set(s.status, (m.get(s.status) ?? 0) + 1);
    if (!m.size) return null;
    return { type: 'doughnut', labels: [...m.keys()], values: [...m.values()], label: 'Streams', colors: PALETTE };
  }, [data]);

  const setStatus = async (status: 'ACTIVE' | 'SUSPENDED') => {
    try {
      await http.patch(`/api/admin/tenants/${id}`, { status });
      ui.toast('success', `Tenant ${status === 'ACTIVE' ? 'activated' : 'suspended'}`);
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Action failed');
    }
  };

  // A temp password with real entropy — crypto, not Math.random. 16 hex chars.
  const tempPassword = () => [...crypto.getRandomValues(new Uint8Array(8))]
    .map((b) => b.toString(16).padStart(2, '0')).join('');

  const submitReset = async () => {
    if (!resetFor) return;
    setResetting(true);
    try {
      await http.post(`/api/admin/tenants/${id}/users/${resetFor.id}/reset-password`, { newPassword });
      ui.toast('success', `Password reset for ${resetFor.email ?? resetFor.id}`);
      setResetFor(null);
      setNewPassword('');
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };
  // Create a stream on a plan straight from here — right after a manual grant,
  // before the tenant ever logs in. The endpoint runs the same service path as
  // the tenant's own "Create stream", so limits and the RTMP/HLS/player link
  // rows come out identical; once a stream exists on the plan the button below
  // disappears (it only renders when the plan has no stream).
  const createStream = async () => {
    if (!creatingFor) return;
    setStreamBusy(true);
    try {
      const created = await http.post<{ rtmpUrl?: string }>(`/api/admin/tenants/${id}/streams`, {
        name: streamName.trim(),
        planId: creatingFor.planId,
      });
      ui.toast('success', `Stream "${streamName.trim()}" created`, created.rtmpUrl);
      setCreatingFor(null);
      setStreamName('');
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Stream creation failed');
    } finally {
      setStreamBusy(false);
    }
  };

  // Grant a plan by hand, or renew one the tenant already holds. Additive: the
  // tenant keeps every other plan, and the limits below re-sum automatically.
  // The server extends an existing row from its current endAt rather than
  // resetting the clock, so a renewal stacks days onto the remaining period.
  // Exception — change of plan (renew modal, a different plan picked): the new
  // row is activated first (no gap), then the superseded row is deactivated.
  const addPlan = async () => {
    if (!granting) return;
    const { plan, cycle, mode, streamId, sub } = granting;
    // Renewing a different plan than the row holds = change of plan.
    const changing = mode === 'renew' && !!sub && plan.id !== sub.planId;
    setPlanBusy(plan.id);
    try {
      const collected = pay.collectPayment ? Number(pay.amount) : 0;
      await http.post(`/api/admin/tenants/${id}/plans/${plan.id}/activate`, {
        billingCycle: cycle,
        days: BILLING_CYCLE_DAYS[cycle],
        streamId: streamId ?? null,
        payment: {
          collectPayment: pay.collectPayment && Number.isFinite(collected) && collected > 0,
          amount: Number.isFinite(collected) ? collected : undefined,
          method: pay.method,
          reference: pay.reference.trim() || undefined,
          // Sent as a full ISO instant: a bare "YYYY-MM-DD" is parsed as UTC
          // midnight and would display as the previous day in local time.
          paidAt: pay.paidAt ? new Date(`${pay.paidAt}T12:00:00`).toISOString() : undefined,
          note: pay.note.trim() || undefined,
        },
      });
      if (changing && sub) {
        // Best effort: the new plan is already active, so a failed cleanup
        // must not throw the grant away — it only leaves an extra row behind
        // for a manual deactivation.
        try {
          await http.delete(`/api/admin/tenants/${id}/plans/${sub.id}`);
        } catch {
          ui.toast('info', `Plan changed, but the old ${sub.name} row is still listed — deactivate it manually`);
        }
      }
      const replaced = changing && sub ? `, replaced ${sub.name}` : '';
      const verb = mode === 'renew' && !changing ? 'renewed' : 'activated';
      ui.toast(
        'success',
        collected > 0 && pay.collectPayment
          ? `${plan.name} ${verb}${replaced} — ${methodLabel(pay.method)} payment recorded`
          : `${plan.name} ${verb}${replaced}`,
      );
      setPlanToAdd('');
      setGranting(null);
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Activation failed');
    } finally {
      setPlanBusy('');
    }
  };

  const removePlan = async (sub: TenantSubscription) => {
    setPlanBusy(sub.id);
    try {
      await http.delete(`/api/admin/tenants/${id}/plans/${sub.id}`);
      ui.toast('success', `${sub.name} deactivated`);
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Deactivation failed');
    } finally {
      setPlanBusy('');
    }
  };

  const deletePlan = async (sub: TenantSubscription) => {
    if (!window.confirm(`Permanently delete "${sub.name}"? This cannot be undone.`)) return;
    setPlanBusy(sub.id);
    try {
      await http.delete(`/api/admin/tenants/${id}/plans/${sub.id}?force=true`);
      ui.toast('success', `${sub.name} deleted`);
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Deletion failed');
    } finally {
      setPlanBusy('');
    }
  };

  // Renew a stream whose plan lapsed (or swap it onto another plan).
  // Always goes through the transaction modal first so a renewal where
  // money changed hands leaves the same payment + invoice record as a
  // manual plan grant. Prefills with what the stream LAST PAID — the same
  // "previous amount" rule the tenant's own RenewDialog shows — falling
  // back to the plan's list price when nothing was collected before.
  const openRenew = (stream: TenantStream) => {
    const plan = planOptionForStream(stream);
    const cycle = (stream.billingCycle ?? 'MONTHLY') as Cycle;
    setPay({
      collectPayment: true,
      amount: String(stream.lastPaidAmount ?? (plan ? priceFor(plan, cycle) : '')),
      method: 'BANK_TRANSFER',
      reference: '',
      paidAt: new Date().toISOString().slice(0, 10),
      note: '',
    });
    setRenewing({ stream, cycle, planId: stream.planId ?? null });
  };

  const submitRenew = async () => {
    if (!renewing) return;
    setPlanBusy(renewing.stream.id);
    try {
      const collected = pay.collectPayment ? Number(pay.amount) : 0;
      await http.post(`/api/admin/tenants/${id}/streams/${renewing.stream.id}/renew`, {
        billingCycle: renewing.cycle,
        // Change of plan: send the picker's selection so renewStream moves the
        // stream and retires the superseded row. Same plan → omitted (the
        // service treats an equal planId as a plain renewal anyway).
        ...(renewing.planId && renewing.planId !== renewing.stream.planId
          ? { planId: renewing.planId }
          : {}),
        payment: {
          collectPayment: pay.collectPayment && Number.isFinite(collected) && collected > 0,
          amount: Number.isFinite(collected) ? collected : undefined,
          method: pay.method,
          reference: pay.reference.trim() || undefined,
          // Sent as a full ISO instant: a bare "YYYY-MM-DD" is parsed as UTC
          // midnight and would display as the previous day in local time.
          paidAt: pay.paidAt ? new Date(`${pay.paidAt}T12:00:00`).toISOString() : undefined,
          note: pay.note.trim() || undefined,
        },
      });
      const moved =
        renewPlan && renewing.planId && renewing.planId !== renewing.stream.planId ? renewPlan.name : null;
      ui.toast(
        'success',
        moved
          ? collected > 0 && pay.collectPayment
            ? `${renewing.stream.name} moved to ${moved} — ${methodLabel(pay.method)} payment recorded`
            : `${renewing.stream.name} moved to ${moved}`
          : collected > 0 && pay.collectPayment
            ? `${renewing.stream.name} renewed — ${methodLabel(pay.method)} payment recorded`
            : `${renewing.stream.name} renewed`,
      );
      setRenewing(null);
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Renewal failed');
    } finally {
      setPlanBusy('');
    }
  };

  // Streams carry only `plan_id`; group them client-side so each plan row can
  // name the streams it is paying for. Called per render — the list is tiny.
  // Streams this tenant-wide row still covers. Once a stream gets its own row it
// is no longer the tenant-wide row's business, so it must not be listed twice.
  const streamsOnPlan = (planId: string) => {
    const bound = new Set(
      (data?.subscriptions ?? []).filter((s) => s.streamId).map((s) => s.streamId as string),
    );
    return (data?.streams ?? []).filter((st) => st.planId === planId && !bound.has(st.id));
  };

  // A stream-bound plan row names exactly one stream; look it up directly so a
  // split row can never drift onto whatever else shares the plan.
  const streamById = (streamId: string) =>
    (data?.streams ?? []).filter((st) => st.id === streamId);

  // The plan a stream is on, as a PlanOption — the catalogue entry, or a
  // synthesis from the stream row when the plan was archived (the catalogue
  // only holds ACTIVE plans), so the renew modal can always price the
  // current plan.
  const planOptionForStream = (st: TenantStream): PlanOption | null => {
    if (!st.planId) return null;
    const known = plans.find((p) => p.id === st.planId);
    if (known) return known;
    return {
      id: st.planId,
      name: st.planName ?? 'Current plan',
      status: 'ARCHIVED',
      // Real list price unknown for an archived plan; the amount the tenant
      // actually paid is the best available figure.
      priceMonth: st.lastPaidAmount ?? 0,
      priceYear: null,
      priceDay: null,
      maxStreams: 0,
      maxDestinationsPerStream: 0,
      maxTotalDestinations: 0,
    };
  };

  // The plan the stream renew modal will charge for — the picker's current
  // selection (the stream's own plan until the admin picks another one).
  const renewPlan = renewing
    ? plans.find((p) => p.id === (renewing.planId ?? renewing.stream.planId)) ?? null
    : null;
  // Change-of-plan flags: renewing, but the selected plan differs from what
  // the row / stream currently holds.
  const streamPlanChanging = !!(renewing && renewing.planId && renewing.planId !== renewing.stream.planId);
  const grantSub = granting?.mode === 'renew' ? (granting.sub ?? null) : null;
  const grantingChanging = !!(grantSub && granting && granting.plan.id !== grantSub.planId);
  // Picker options for the grant modal: the row's current plan first (even
  // if archived), then every other plan in the catalogue.
  const renewPlanOptions: PlanOption[] = grantSub
    ? [
        plans.find((p) => p.id === grantSub.planId) ?? planOptionFromSub(grantSub),
        ...plans.filter((p) => p.id !== grantSub.planId),
      ]
    : [];
  // Picker options for the stream renew modal, same rule via the stream row.
  const streamCurrentPlan = renewing ? planOptionForStream(renewing.stream) : null;
  const streamRenewPlanOptions: PlanOption[] = renewing
    ? [
        ...(streamCurrentPlan ? [streamCurrentPlan] : []),
        ...plans.filter((p) => p.id !== renewing.stream.planId),
      ]
    : [];

  const splitPlan = async (sub: TenantSubscription) => {
    setPlanBusy(sub.id);
    try {
      const r = await http.post<{ created: number }>(
        `/api/admin/tenants/${id}/plans/${sub.id}/split-per-stream`,
      );
      ui.toast(
        r.created ? 'success' : 'info',
        r.created
          ? `Split into ${r.created} separate plan${r.created === 1 ? '' : 's'} — renew each one on its own`
          : 'Every stream on this plan already has its own row',
      );
      setData(await http.get<TenantDetail>(`/api/admin/tenants/${id}`));
    } catch (e) {
      ui.toast('danger', e instanceof Error ? e.message : 'Split failed');
    } finally {
      setPlanBusy('');
    }
  };

  const streamUrlCols: Column<TenantStream>[] = [
    { key: 'stream', header: 'Stream', render: (s) => (
      <div><div className="font-medium">{s.name}</div><StatusBadge status={s.status} /></div>
    ) },
    { key: 'ingest', header: 'RTMP ingest', render: (s) => (
      <div className="url-cell"><code>{s.ingestUrl}</code><CopyButton text={s.ingestUrl} /></div>
    ) },
    { key: 'hls', header: 'HLS output', render: (s) => (
      <div className="url-cell"><code>{s.hlsUrl}</code><CopyButton text={s.hlsUrl} /></div>
    ) },
    { key: 'player', header: 'Player', render: (s) => (
      <div className="url-cell"><a href={s.playerUrl} target="_blank" rel="noreferrer">{s.playerUrl}</a><CopyButton text={s.playerUrl} /></div>
    ) },
    { key: 'plan', header: 'Plan activated', render: (s) => (
      <div className="text-sm">
        <div className="font-medium">{(s as any).planName ?? (s as any).planId ?? '—'}</div>
        {(s as any).renewsAt && <div className="text-muted">Renews {longDate((s as any).renewsAt)}{(s as any).billingCycle ? ` · ${(s as any).billingCycle.toLowerCase()}` : ''}</div>}
        {(s as any).lastPaidAmount != null && <div className="text-muted">Last paid {(s as any).lastPaidAmount.toLocaleString('en-IN', { style: 'currency', currency: (s as any).lastPaidCurrency || 'INR', maximumFractionDigits: 0 })}</div>}
      </div>
    ) },
    { key: 'embed', header: 'Embed', render: (s) => <CopyButton text={s.embedUrl} label="Copy iframe" /> },
  ];

  const streamStatCols: Column<TenantStream>[] = [
    { key: 'name', header: 'Stream', render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'status', header: 'Status', render: (s) => <StatusBadge status={s.status} /> },
    { key: 'live', header: 'Current uptime', render: (s) => <span className="text-sm">{fmtDur(s.durationSeconds)}</span> },
    { key: 'total', header: 'All-time uptime', render: (s) => <span className="text-sm">{fmtDur(s.totalDurationSeconds)}</span> },
    { key: 'bytes', header: 'Data', render: (s) => <span className="text-sm">{mb(s.totalBytes)} MB</span> },
    { key: 'peak', header: 'Peak / avg', render: (s) => (
      <span className="text-sm text-muted">{s.peakBitrateKbps ?? '—'} / {s.avgBitrateKbps ?? '—'} kbps</span>
    ) },
    { key: 'dest', header: 'Destinations', render: (s) => <span className="text-sm">{s.destinations}</span> },
    { key: 'rec', header: 'Recording', render: (s) => <span className="text-sm">{s.recordingEnabled ? 'On' : 'Off'}</span> },
  ];

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="admin-page">
        <div className="page-header admin-page-header">
          <div>
            <h1 className="page-title">
              <Link to="/admin/tenants" className="tenant-link-plain">Tenants</Link> / {data?.tenant.name ?? '…'}
            </h1>
            <p className="page-subtitle">
              {data?.tenant.slug} · joined {data ? longDate(data.tenant.createdAt) : '—'}
              {data && <> <StatusBadge status={data.tenant.status} /></>}
            </p>
          </div>
          {data && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => void setStatus(data.tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')}>
                {data.tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
              </Button>
              <Button onClick={() => navigate('/admin/tenants')}>Back to list</Button>
            </div>
          )}
        </div>

        {loading && <SpinnerBlock />}
        {error && <div className="alert danger">{error}</div>}

        {data && (
          <>
            <div className="stats-grid">
              <MetricCard label="Streams" value={data.totals.streams} tone="primary" hint={`${data.totals.liveStreams} live now`} />
              <MetricCard label="Sessions" value={data.totals.sessions} tone="info" hint="All-time" />
              <MetricCard label="Hours streamed" value={hrs(data.totals.durationSeconds)} tone="success" hint="All-time" />
              <MetricCard label="Data transferred" value={`${mb(data.totals.bytes)} MB`} tone="warning" hint="All-time ingest" />
              <MetricCard label="Active destinations" value={data.activeDestinations} tone="info" hint="Connected relays" />
              <MetricCard label="Revenue" value={data.totals.paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })} tone="success" hint="Paid payments" />
            </div>

            {/* Plan control sits directly under the metrics: granting or revoking
                a plan is the most common admin action here, and it used to be
                buried below the spectrogram, four charts and two tables. */}
            <div className="detail-grid">
              <section className="detail-section">
                <h4 className="section-title">Plans &amp; Subscription</h4>
                <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                  A tenant can hold several plans at once. Limits below are the sum across every
                  unexpired active plan.
                </p>

                {data.subscriptions.length ? (
                  <ul className="detail-users">
                    {data.subscriptions.map((s) => (
                      <li key={s.id}>
                        <div style={{ minWidth: 0 }}>
                          <span className="font-medium">{s.name}</span>{' '}
                          <StatusBadge status={s.expired && s.status === 'ACTIVE' ? 'EXPIRED' : s.status} />
                          <div className="text-sm text-muted">
                            {s.billingCycle?.toLowerCase()} · {s.endAt ? `ends ${longDate(s.endAt)}` : 'no end date'}
                          </div>
                          {/* Each row buys one stream, so it renews one stream.
                              A tenant-wide row (streamId null) still covers every
                              stream pointing at this plan — splitting it gives
                              each one its own deactivate control. Renewing (and
                              recording the payment) happens from the
                              "Streams with no active plan" list below. */}
                          {(s.streamId ? streamById(s.streamId) : streamsOnPlan(s.planId)).map((st) => (
                              <div key={st.id} style={{ marginTop: 6 }}>
                                <div className="text-sm">
                                  <span className="font-medium">{st.name}</span>{' '}
                                  <StatusBadge status={st.status} />
                                </div>
                                <div className="url-cell text-sm">
                                  <code>{st.ingestUrl}</code><CopyButton text={st.ingestUrl} />
                                </div>
                                <div className="url-cell text-sm">
                                  <code>{st.hlsUrl}</code><CopyButton text={st.hlsUrl} />
                                </div>
                            </div>
                          ))}
                          {/* The superseded tenant-wide row (the split's source)
                              and cancelled leftovers are filtered out of the
                              API response, so only this static hint is left. */}
                          {!s.streamId && !streamsOnPlan(s.planId).length && (
                            <div className="text-sm text-muted" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              No stream created on this plan yet.
                              {s.status === 'ACTIVE' && !s.expired && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => { setCreatingFor(s); setStreamName(s.name); }}
                                >
                                  Create stream…
                                </Button>
                              )}
                            </div>
                          )}
                          {!s.streamId && streamsOnPlan(s.planId).length > 1 && (
                            <div style={{ marginTop: 6 }}>
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Give each stream its own row so they renew separately"
                                disabled={planBusy === s.id}
                                onClick={() => void splitPlan(s)}
                              >
                                {planBusy === s.id ? 'Splitting…' : 'Split into separate plans'}
                              </Button>
                            </div>
                          )}
                        </div>
                        {s.status === 'ACTIVE' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              title={`Extend ${s.name} and record the payment`}
                              disabled={planBusy === s.id}
                              onClick={() => {
                                // Renew from the row's own plan data — the catalogue
                                // only lists ACTIVE plans, so an archived plan used
                                // to turn this button into an error toast.
                                const plan = plans.find((p) => p.id === s.planId) ?? planOptionFromSub(s);
                                openPayment(plan, (s.billingCycle as Cycle) ?? 'MONTHLY', 'renew', s.streamId ?? null, s);
                              }}
                            >
                              Renew…
                            </Button>
                            {!s.expired && (
                              <>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  title={`Deactivate ${s.name} — other plans are unaffected`}
                                  disabled={planBusy === s.id}
                                  onClick={() => void removePlan(s)}
                                >
                                  {planBusy === s.id ? 'Removing…' : 'Deactivate'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  title={`Permanently delete ${s.name} — cannot be undone`}
                                  disabled={planBusy === s.id}
                                  onClick={() => void deletePlan(s)}
                                >
                                  {planBusy === s.id ? 'Deleting…' : 'Delete'}
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-muted text-sm">No plans — this tenant is blocked from creating streams.</p>}

                <dl className="detail-dl">
                  <dt>Streams</dt><dd>{data.limits.maxStreams}</dd>
                  <dt>Destinations / stream</dt><dd>{data.limits.maxDestinationsPerStream || 'Unlimited'}</dd>
                  <dt>Total destinations</dt><dd>{data.limits.maxTotalDestinations || 'Unlimited'}</dd>
                  <dt>Recording</dt><dd>{data.limits.recordingEnabled ? 'Enabled' : 'Disabled'}</dd>
                  <dt>Tenant created</dt><dd>{longDate(data.tenant.createdAt)}</dd>
                </dl>

                {/* Streams the tenant still sees but no active plan pays for
                    (its plan was deactivated or expired). `visibleSubscriptions`
                    drops those rows, so without this list the stream vanishes
                    from the plan section while the tenant page still shows it. */}
                {data.streams.some((st) => !st.planActive) ? (
                  <div className="text-sm" style={{ marginTop: 12 }}>
                    <div className="font-medium" style={{ marginBottom: 6 }}>Streams with no active plan</div>
                    {data.streams.filter((st) => !st.planActive).map((st) => (
                      <div key={st.id} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span className="font-medium">{st.name}</span>
                          <StatusBadge status={st.status} />
                          <span className="text-muted">{st.planName ? `plan ${st.planName} deactivated` : 'no plan set'}</span>
                        </div>
                        <div className="url-cell text-sm">
                          <code>{st.ingestUrl}</code><CopyButton text={st.ingestUrl} />
                        </div>
                        <div className="url-cell text-sm">
                          <code>{st.hlsUrl}</code><CopyButton text={st.hlsUrl} /><Button size="sm" variant="primary" onClick={() => openRenew(st)} disabled={planBusy === st.id}>
                            {planBusy === st.id ? 'Renewing…' : 'Renew'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="flex gap-2 items-end" style={{ marginTop: 12 }}>
                  <Select
                    label="Add a plan"
                    value={planToAdd}
                    onChange={setPlanToAdd}
                    options={[
                      { value: '', label: 'Add a plan…' },
                      ...plans.map((p) => ({
                        value: p.id,
                        label: `${p.name} — ${p.maxStreams} streams, ${p.maxDestinationsPerStream || '∞'} dest/stream`,
                      })),
                    ]}
                  />
                  <Button
                    onClick={() => {
                      const plan = plans.find((p) => p.id === planToAdd);
                      if (!plan) return;
                      openPayment(plan, 'MONTHLY', 'grant');
                    }}
                    disabled={!planToAdd || planBusy === planToAdd}
                  >
                    {planBusy && planBusy === planToAdd ? 'Activating…' : 'Activate…'}
                  </Button>
                </div>
              </section>
              <section className="detail-section">
                <h4 className="section-title">Team members</h4>
                {data.users.length ? (
                  <ul className="detail-users">
                    {data.users.map((u) => (
                      <li key={u.id}>
                        <span className="user-email">{u.email ?? u.id}</span>
                        <span className={`role-pill ${u.role === 'OWNER' ? 'owner' : ''}`}>{u.role}</span>
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Set a new password for this member"
                          onClick={() => { setNewPassword(tempPassword()); setResetFor({ id: u.id, email: u.email }); }}
                        >
                          Reset password
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-muted text-sm">No members</p>}
              </section>
            </div>

            <Card title="Activity spectrogram — last 30 days">
              <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                One cell per day, shaded by hours streamed. Hover a cell for exact figures.
              </p>
              <Spectrogram days={spectroDays} label="Streaming activity over the last 30 days" />
            </Card>

            <ChartCard title="Hours streamed per day" data={dailyHours} />
            <ChartCard title="Data transferred per day (MB)" data={dailyData} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
              <ChartCard title="Bandwidth share per stream (all-time)" data={streamMix} />
              <ChartCard title="Stream status mix" data={statusMix} />
            </div>

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/streams`)}>
                View streams
              </Button>
            </div>

            <Card title="All stream URLs">
              <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                The ingest URL shows the path only — the stream key stays secret. HLS and player URLs work as soon as the stream is on air.
              </p>
              {data.streams.length
                ? <DataTable columns={streamUrlCols} rows={data.streams} />
                : <EmptyState title="No streams" body="This tenant has not created any streams yet." />}
            </Card>

            <Card title="Stream statistics">
              {data.streams.length
                ? <DataTable columns={streamStatCols} rows={data.streams} />
                : <p className="text-muted text-sm">No streams</p>}
            </Card>

            <Card title="Transaction history">
              <p className="text-muted text-sm" style={{ marginTop: 0 }}>
                Every payment recorded against this tenant — online (Razorpay) and cash/bank
                collected by hand when a plan was activated manually.
              </p>
              {data.payments.length ? (
                <DataTable
                  columns={[
                    { key: 'date', header: 'Paid on', render: (p) => (
                      <span className="text-sm">
                        {longDate(p.paidAt ?? p.createdAt)}
                        {p.paidAt && p.paidAt.slice(0, 10) !== p.createdAt.slice(0, 10) ? (
                          <span className="text-muted text-xs" title="Recorded later than the payment date">
                            {' '}· booked {longDate(p.createdAt)}
                          </span>
                        ) : null}
                      </span>
                    ) },
                    { key: 'plan', header: 'Plan', render: (p) => (
                      <span className="text-sm">
                        {p.planName ?? '—'}
                        {p.billingCycle ? (
                          <span className="text-muted text-xs"> · {p.billingCycle.toLowerCase()}</span>
                        ) : null}
                      </span>
                    ) },
                    { key: 'stream', header: 'Stream', render: (p) => (
                      <span className="text-sm text-muted">{p.streamName ?? 'Stream not created'}</span>
                    ) },
                    { key: 'method', header: 'Method', render: (p) => (
                      <span className="text-sm">{methodLabel(p.method)}</span>
                    ) },
                    { key: 'amount', header: 'Amount', render: (p) => (
                      <span className="text-sm font-medium">
                        {p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} {p.currency}
                      </span>
                    ) },
                    { key: 'status', header: 'Status', render: (p) => <StatusBadge status={p.status} /> },
                    { key: 'ref', header: 'Reference', render: (p) => (
                      <span className="text-sm text-muted">
                        {p.receipt ?? p.providerRef ?? '—'}
                        {p.provider === 'MANUAL' ? <span className="text-xs"> · manual</span> : null}
                        {p.note ? <div className="text-xs">{p.note}</div> : null}
                      </span>
                    ) },
                  ]}
                  rows={data.payments}
                />
              ) : <p className="text-muted text-sm">No payments yet</p>}
            </Card>
          </>
        )}

        {granting && (
          <Modal
            title={grantingChanging ? `Change to ${granting.plan.name}` : `${granting.mode === 'renew' ? 'Renew' : 'Activate'} ${granting.plan.name}`}
            onClose={() => setGranting(null)}
            footer={
              <>
                <Button variant="secondary" onClick={() => setGranting(null)}>Cancel</Button>
                {/* A plan CHANGE must leave a transaction trail — the old row
                    is deactivated afterwards, so "without payment" is not offered. */}
                <Button
                  onClick={() => void addPlan()}
                  disabled={
                    planBusy === granting.plan.id ||
                    (pay.collectPayment && !(Number(pay.amount) > 0)) ||
                    (grantingChanging && !pay.collectPayment)
                  }
                >
                  {planBusy === granting.plan.id
                    ? 'Saving…'
                    : grantingChanging
                      ? 'Record payment & change plan'
                      : pay.collectPayment
                        ? `Record payment & ${granting.mode === 'renew' ? 'renew' : 'activate'}`
                        : `${granting.mode === 'renew' ? 'Renew' : 'Activate'} without payment`}
                </Button>
              </>
            }
          >
            <p className="text-muted text-sm" style={{ marginTop: 0 }}>
              {grantSub && grantingChanging ? (
                <>
                  Switching <strong>{data?.tenant.name}</strong> from{' '}
                  <strong>{grantSub.name}</strong> to{' '}
                  <strong>{granting.plan.name}</strong>. The new plan is activated first —
                  no gap — then the old row is deactivated so it stops billing.
                </>
              ) : granting.mode === 'renew' ? (
                <>
                  Extending <strong>{granting.plan.name}</strong> for{' '}
                  <strong>{data?.tenant.name}</strong>. Days are added onto the time already
                  left on the plan, so nothing is lost if it has not run out yet.
                </>
              ) : (
                <>
                  Granting <strong>{granting.plan.name}</strong> to <strong>{data?.tenant.name}</strong>{' '}
                  ({granting.plan.maxStreams} stream{granting.plan.maxStreams === 1 ? '' : 's'}).
                  If the tenant already holds this plan the extra days are added to it.
                </>
              )}{' '}
              Record how it was paid so it appears in the transaction history and revenue totals.
            </p>

            {/* Renewal context: what the tenant held and last paid — the row
                itself is authoritative (its plan may be archived). */}
            {grantSub && (
              <div
                className="text-sm"
                style={{
                  margin: '0 0 12px',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '4px 12px',
                }}
              >
                <span className="text-muted">Previous plan</span>
                <span>
                  {grantSub.name}
                  {grantSub.streamId ? '' : ' (all streams)'} · {grantSub.billingCycle?.toLowerCase() ?? ''}
                  {grantSub.endAt ? ` · valid until ${longDate(grantSub.endAt)}` : ''}
                </span>
                <span className="text-muted">Last paid</span>
                <span>
                  {grantSub.lastPaid
                    ? `${money(grantSub.lastPaid.amount, grantSub.lastPaid.currency)} on ${longDate(grantSub.lastPaid.paidAt)}`
                    : 'No payment recorded yet'}
                </span>
              </div>
            )}

            {/* Plan picker: a renewal can target a different plan (change of
                plan) — each option shows the price this cycle bills at. */}
            {granting.mode === 'renew' && renewPlanOptions.length > 1 && (
              <Select
                label="Plan"
                value={granting.plan.id}
                onChange={(v) => {
                  const next = renewPlanOptions.find((p) => p.id === v);
                  if (!next) return;
                  setGranting({ ...granting, plan: next });
                  // Back to the row's own plan keeps the last-collected amount;
                  // any other plan bills at its own catalogue price for this cycle.
                  const backToRow = !!(grantSub && next.id === grantSub.planId);
                  setPay({
                    ...pay,
                    amount: String(
                      backToRow && grantSub?.lastPaid
                        ? grantSub.lastPaid.amount
                        : priceFor(next, granting.cycle),
                    ),
                  });
                }}
                options={renewPlanOptions.map((p) => ({
                  value: p.id,
                  label: `${p.name} — ${money(priceFor(p, granting.cycle), p.currency)}${
                    grantSub && p.id === grantSub.planId ? ' · current' : ''
                  }`,
                }))}
              />
            )}

            <Select
              label="Billing cycle"
              value={granting.cycle}
              onChange={(v) => {
                const cycle = v as Cycle;
                setGranting({ ...granting, cycle });
                // Re-price for the new cycle; the hint below shows this figure.
                setPay({ ...pay, amount: String(priceFor(granting.plan, cycle)) });
              }}
              options={[
                ...(granting.plan.priceDay != null
                  ? [{ value: 'DAILY', label: 'Daily — 1 day' }]
                  : []),
                { value: 'MONTHLY', label: 'Monthly — 30 days' },
                { value: 'YEARLY', label: 'Yearly — 365 days' },
              ]}
            />

            <label className="flex gap-2 items-center" style={{ margin: '12px 0' }}>
              <input
                type="checkbox"
                checked={pay.collectPayment}
                onChange={(e) => setPay({ ...pay, collectPayment: e.target.checked })}
              />
              <span className="text-sm">Payment was collected for this plan</span>
            </label>
            <div className="text-muted text-xs" style={{ marginTop: -6, marginBottom: 12 }}>
              Untick only for a free add-on or a comped month — nothing is added to revenue.
            </div>
            {grantingChanging && !pay.collectPayment && (
              <div className="text-muted text-xs" style={{ marginTop: -6, marginBottom: 12 }}>
                A plan change requires payment details — record what was collected for the new plan.
              </div>
            )}

            {pay.collectPayment && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input
                    label="Amount received"
                    type="number"
                    value={pay.amount}
                    onChange={(v) => setPay({ ...pay, amount: v })}
                    placeholder="0.00"
                    hint={
                      grantSub?.lastPaid && grantSub.planId === granting.plan.id
                        ? `Last paid ${money(grantSub.lastPaid.amount, grantSub.lastPaid.currency)} · plan price ${money(priceFor(granting.plan, granting.cycle), granting.plan.currency)} — edit for a discount.`
                        : `Plan price ${money(priceFor(granting.plan, granting.cycle), granting.plan.currency)} — edit for a discount.`
                    }
                  />
                  <Select
                    label="Payment method"
                    value={pay.method}
                    onChange={(v) => setPay({ ...pay, method: v })}
                    options={PAY_METHODS}
                  />
                  <Input
                    label="Transaction / UTR reference"
                    value={pay.reference}
                    onChange={(v) => setPay({ ...pay, reference: v })}
                    placeholder="e.g. UTR402198337421"
                    hint="Bank UTR, gateway txn id or receipt number. Optional."
                  />
                  <Input
                    label="Paid on"
                    type="date"
                    value={pay.paidAt}
                    onChange={(v) => setPay({ ...pay, paidAt: v })}
                  />
                </div>
                <div className="mt-2">
                  <Textarea
                    label="Note"
                    value={pay.note}
                    onChange={(v) => setPay({ ...pay, note: v })}
                    placeholder="Anything worth remembering — e.g. partial month, cash collected at the office."
                    rows={2}
                  />
                </div>
              </>
            )}
          </Modal>
        )}

        {renewing && (
          <Modal
            title={`Renew ${renewing.stream.name}`}
            onClose={() => setRenewing(null)}
            footer={
              <>
                <Button variant="secondary" onClick={() => setRenewing(null)}>Cancel</Button>
                {/* A plan CHANGE must leave a transaction trail, same as the
                    grant modal — "without payment" is not offered for it. */}
                <Button
                  onClick={() => void submitRenew()}
                  disabled={
                    planBusy === renewing.stream.id ||
                    (pay.collectPayment && !(Number(pay.amount) > 0)) ||
                    (streamPlanChanging && !pay.collectPayment)
                  }
                >
                  {planBusy === renewing.stream.id
                    ? 'Saving…'
                    : streamPlanChanging
                      ? 'Record payment & change plan'
                      : pay.collectPayment
                        ? 'Record payment & renew'
                        : 'Renew without payment'}
                </Button>
              </>
            }
          >
            <p className="text-muted text-sm" style={{ marginTop: 0 }}>
              Extending the plan for <strong>{renewing.stream.name}</strong> on{' '}
              <strong>{data?.tenant.name}</strong>. Days are added onto the time already
              left, so nothing is lost if it has not run out yet. Record how it was
              paid so it appears in the transaction history and revenue totals.
            </p>

            {/* Renewal context: what this stream held and last paid. */}
            <div
              className="text-sm"
              style={{
                margin: '0 0 12px',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '4px 12px',
              }}
            >
              <span className="text-muted">Current plan</span>
              <span>
                {renewing.stream.planName ?? '—'}
                {renewing.stream.billingCycle ? ` · ${renewing.stream.billingCycle.toLowerCase()}` : ''}
              </span>
              <span className="text-muted">Renews until</span>
              <span>{renewing.stream.renewsAt ? longDate(renewing.stream.renewsAt) : '—'}</span>
              <span className="text-muted">Last paid</span>
              <span>
                {renewing.stream.lastPaidAmount != null
                  ? money(renewing.stream.lastPaidAmount, renewing.stream.lastPaidCurrency)
                  : 'No payment recorded yet'}
              </span>
            </div>

            {/* Plan picker: a renewal can move the stream onto another plan
                (change of plan) — each option shows this cycle's price. */}
            {streamRenewPlanOptions.length > 1 && (
              <Select
                label="Plan"
                value={renewing.planId ?? renewing.stream.planId ?? ''}
                onChange={(v) => {
                  const next = streamRenewPlanOptions.find((p) => p.id === v);
                  if (!next) return;
                  setRenewing({ ...renewing, planId: next.id });
                  // Back to the current plan keeps what was last paid; any other
                  // plan bills at its own catalogue price for this cycle.
                  const backToCurrent = next.id === renewing.stream.planId;
                  setPay({
                    ...pay,
                    amount: String(
                      backToCurrent && renewing.stream.lastPaidAmount != null
                        ? renewing.stream.lastPaidAmount
                        : priceFor(next, renewing.cycle),
                    ),
                  });
                }}
                options={streamRenewPlanOptions.map((p) => ({
                  value: p.id,
                  label: `${p.name} — ${money(priceFor(p, renewing.cycle), p.currency)}${
                    p.id === renewing.stream.planId ? ' · current' : ''
                  }`,
                }))}
              />
            )}

            <Select
              label="Billing cycle"
              value={renewing.cycle}
              onChange={(v) => {
                const cycle = v as Cycle;
                setRenewing({ ...renewing, cycle });
                // Re-price for the new cycle; the hint below shows this figure.
                if (renewPlan) setPay({ ...pay, amount: String(priceFor(renewPlan, cycle)) });
              }}
              options={[
                ...(renewPlan?.priceDay != null
                  ? [{ value: 'DAILY', label: 'Daily — 1 day' }]
                  : []),
                { value: 'MONTHLY', label: 'Monthly — 30 days' },
                { value: 'YEARLY', label: 'Yearly — 365 days' },
              ]}
            />

            <label className="flex gap-2 items-center" style={{ margin: '12px 0' }}>
              <input
                type="checkbox"
                checked={pay.collectPayment}
                onChange={(e) => setPay({ ...pay, collectPayment: e.target.checked })}
              />
              <span className="text-sm">Payment was collected for this renewal</span>
            </label>
            <div className="text-muted text-xs" style={{ marginTop: -6, marginBottom: 12 }}>
              Untick only for a free renewal or a comped period — nothing is added to revenue.
            </div>
            {streamPlanChanging && !pay.collectPayment && (
              <div className="text-muted text-xs" style={{ marginTop: -6, marginBottom: 12 }}>
                A plan change requires payment details — record what was collected for the new plan.
              </div>
            )}

            {pay.collectPayment && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input
                    label="Amount received"
                    type="number"
                    value={pay.amount}
                    onChange={(v) => setPay({ ...pay, amount: v })}
                    placeholder="0.00"
                    hint={
                      renewing.stream.lastPaidAmount != null
                        ? `Last paid ${money(renewing.stream.lastPaidAmount, renewing.stream.lastPaidCurrency)}${
                            renewPlan ? ` · plan price ${money(priceFor(renewPlan, renewing.cycle), renewPlan.currency)}` : ''
                          } — edit for a discount.`
                        : renewPlan
                          ? `Plan price ${money(priceFor(renewPlan, renewing.cycle), renewPlan.currency)} — edit for a discount.`
                          : undefined
                    }
                  />
                  <Select
                    label="Payment method"
                    value={pay.method}
                    onChange={(v) => setPay({ ...pay, method: v })}
                    options={PAY_METHODS}
                  />
                  <Input
                    label="Transaction / UTR reference"
                    value={pay.reference}
                    onChange={(v) => setPay({ ...pay, reference: v })}
                    placeholder="e.g. UTR402198337421"
                    hint="Bank UTR, gateway txn id or receipt number. Optional."
                  />
                  <Input
                    label="Paid on"
                    type="date"
                    value={pay.paidAt}
                    onChange={(v) => setPay({ ...pay, paidAt: v })}
                  />
                </div>
                <div className="mt-2">
                  <Textarea
                    label="Note"
                    value={pay.note}
                    onChange={(v) => setPay({ ...pay, note: v })}
                    placeholder="Anything worth remembering — e.g. partial month, cash collected at the office."
                    rows={2}
                  />
                </div>
              </>
            )}
          </Modal>
        )}

        {creatingFor && (
          <Modal
            title={`Create stream for ${creatingFor.name}`}
            onClose={() => { if (!streamBusy) { setCreatingFor(null); setStreamName(''); } }}
            footer={
              <>
                <Button variant="secondary" disabled={streamBusy} onClick={() => { setCreatingFor(null); setStreamName(''); }}>Cancel</Button>
                <Button disabled={streamBusy || !streamName.trim()} onClick={() => void createStream()}>
                  {streamBusy ? 'Creating…' : 'Create stream'}
                </Button>
              </>
            }
          >
            <p className="text-muted text-sm" style={{ marginTop: 0 }}>
              Creates the stream on this plan together with its RTMP ingest, HLS and player links.
              Once a stream exists on the plan, this button disappears.
            </p>
            <Input
              label="Stream name"
              value={streamName}
              onChange={setStreamName}
              placeholder="e.g. Main"
              hint="Becomes part of the ingest URL, e.g. rtmp://…/main/live — pick something stable."
            />
          </Modal>
        )}

        {resetFor && (
          <Modal
            title="Set a new password"
            onClose={() => { setResetFor(null); setNewPassword(''); }}
            footer={
              <>
                <Button variant="secondary" onClick={() => { setResetFor(null); setNewPassword(''); }}>Cancel</Button>
                <Button onClick={() => void submitReset()} disabled={resetting || newPassword.length < 8}>
                  {resetting ? 'Saving…' : 'Set password'}
                </Button>
              </>
            }
          >
            <p className="text-muted text-sm" style={{ marginTop: 0 }}>
              To be used when the tenant has forgotten their password — the current one cannot be shown,
              because only a one-way hash is stored. All of this member&apos;s active sessions will be signed out.
            </p>
            <p className="text-sm"><strong>{resetFor.email ?? resetFor.id}</strong></p>
            <Input
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              hint="At least 8 characters. Copy it now — it is not stored anywhere in readable form."
              autoComplete="new-password"
            />
            <div className="flex gap-2 items-center mt-2">
              <CopyButton text={newPassword} label="Copy password" />
              <Button size="sm" variant="ghost" onClick={() => setNewPassword(tempPassword())}>Generate another</Button>
            </div>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
}




